import classNames from "classnames";
import React from "react";

import { Button, ButtonVariant } from "../newUI/button";
import { Modal } from "./Modal";

type Variant = "danger" | "warning" | "primary" | "success";

type Props = {
  onClose: () => void;
  onConfirm: () => void;
  icon: React.ReactNode;
  title: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: Variant;
  iconVariant?: Variant;
  isLoading?: boolean;
  noCancel?: boolean;
  confirmButtonStyles?: string;
  confirmFirst?: boolean;
  /** Extra content under the description (e.g. a "move work to" picker). */
  children?: React.ReactNode;
};

export const SettingsConfirmModal: React.FC<Props> = ({
  onClose,
  onConfirm,
  icon,
  title,
  description,
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "danger",
  iconVariant,
  isLoading,
  noCancel,
  confirmButtonStyles,
  confirmFirst,
  children,
}) => {
  const confirmButtonVariant: Record<Variant, ButtonVariant> = {
    danger: "danger",
    warning: "primary",
    primary: "primary",
    success: "success",
  };

  const confirmIconStyle: Record<Variant, string> = {
    danger: "bg-[#FEE2E2] text-[#DC2626]",
    warning: "bg-[#FFFBE5] text-[#FAAF0E]",
    primary: "bg-[#7C66F0AB] text-[#ffffff]",
    success: "bg-[#D1FAE5] text-[#10B981]",
  };

  const resolvedIconVariant = iconVariant || variant;

  const confirmButton = (
    <Button
      variant={confirmButtonVariant[variant]}
      loading={isLoading}
      onClick={onConfirm}
      autoWidth
      className={classNames(
        confirmButtonStyles
          ? confirmButtonStyles
          : "flex justify-center px-4 py-2",
        !confirmButtonStyles && (noCancel ? "w-1/2" : "w-full"),
      )}>
      {confirmText}
    </Button>
  );

  const cancelButton = !noCancel ? (
    <Button
      variant={"outline"}
      fullWidth
      disabled={isLoading}
      onClick={onClose}>
      {cancelText}
    </Button>
  ) : null;

  return (
    <Modal onClose={onClose} width={"440px"}>
      <div
        className={classNames(
          "mb-3 flex h-12 w-12 items-center justify-center rounded-full",
          confirmIconStyle[resolvedIconVariant],
        )}>
        {icon}
      </div>

      <p className={"mb-2 text-xl font-semibold text-[#1D2C3B]"}>{title}</p>

      {description && (
        <p
          className={"text-sm text-[#5F758F]"}
          dangerouslySetInnerHTML={{ __html: description }}
        />
      )}

      {children}

      <div
        className={classNames(
          "mt-8 flex gap-3 text-sm font-semibold",
          noCancel ? "justify-end" : "justify-center",
        )}>
        {confirmFirst ? confirmButton : cancelButton}
        {confirmFirst ? cancelButton : confirmButton}
      </div>
    </Modal>
  );
};
